import React, { useState, useEffect } from "react";
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Camera, { FACING_MODES, IMAGE_TYPES } from "react-html5-camera-photo";
import 'react-html5-camera-photo/build/css/index.css';
import arrow1 from "../../../assets/Icons/left-top.png"
import arrow2 from "../../../assets/Icons/right-top.png"
import arrow3 from "../../../assets/Icons/left-bottom.png"
import arrow4 from "../../../assets/Icons/right-bottom.png"
import PermissionPopup from '../PermissionPopup';
import FrontCameraError from './frontCameraError';
import Preview from '../Preview';
import { GetMobileOperatingSystem } from "../../../Utils/getOS";
import demoCapture from "../../../assets/unilever-media/lady.png";
import shadow from "../../../assets/unilever-media/shadow.png"
import { logData, sendToDevelopers } from "../../../Services/APIs/scan";
import { getCompleteDeviceDetails } from "../../../Utils/deviceDetails";

const CameraCapture = () => {
	const navigate = useNavigate();
	const [facialMode, setFacialMode] = useState(FACING_MODES.USER);
	const [capturedImage, setCapturedImage] = useState();
	const [frontCameraError, setFrontCameraError] = useState(false);
	const [errorType, setErrorType] = useState({ isError: false, type: "ios" });
	const OS = GetMobileOperatingSystem();
	const [mediaStream, setStream] = useState(null);
	const [showDemoScreen, setShowDemoScreen] = useState(true);
	const uid = JSON.parse(localStorage.getItem("usertpi") || "{}").usertpi;
	const company = JSON.parse(localStorage.getItem("companyBucket") || "{}").companyBucket;

	useEffect(() => {
		const isScanCompleted = localStorage.getItem("isScanCompleted");
		if (!uid || !company || isScanCompleted) {
			navigate("/unilever");
		}

		// Cleanup function to stop camera when component unmounts
		return () => {
			if (mediaStream) {
				mediaStream.getTracks().forEach(track => {
					track.stop();
				});
			}
		};
	}, [navigate, capturedImage, mediaStream]);

	const handleTakePhoto = (dataUri) => {
		logData({ uid, company, message: "captured image" })
		setCapturedImage(dataUri);
	};

	const handleBack = () => {
		window.history.back();
	};

	const OvuleShape = () => {
		return (
			<div className={`ovule-container h-full w-full flex flex-col justify-center items-center`}>
				<div className='bg-white absolute top-20 text-xs font-urbanist font-semibold rounded-full px-4 py-3 sm600:text-2xl'>
					Tap anywhere to capture
				</div>
				<div className="relative w-[100%] h-[80%]">
					{/* Face Outline */}
					<div className="absolute top-[60%] w-[70%] h-[160px] left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-4 border-white rounded-lg bg-transparent" />

				</div>
			</div>
		);
	};

	const TopLayer = () => {
		return (
			<div className='top-0 left-0 bg-slate-600 text-white w-full z-50'>
				{/* Back Button Top Left */}
				<div className='absolute top-0 left-0 h-13 p-4'>
					<button type='button' onClick={() => { window.history.back() }}>
						<img src={arrow1} alt="back" className="w-8 h-8" />
					</button>
				</div>

				{/* Back Button Top Right */}
				<div className='absolute top-0 right-0 h-13 p-4'>
					<button type='button' onClick={() => { window.history.back() }}>
						<img src={arrow2} alt="back" className="w-8 h-8" />
					</button>
				</div>

				{/* Back Button Bottom Left */}
				<div className='absolute bottom-0 left-0 p-4'>
					<button type='button' onClick={() => { window.history.back() }}>
						<img src={arrow3} alt="back" className="w-8 h-8" />
					</button>
				</div>

				{/* Back Button Bottom Right */}
				<div className='absolute bottom-0 right-0 p-4'>
					<button type='button' onClick={() => { window.history.back() }}>
						<img src={arrow4} alt="back" className="w-8 h-8" />
					</button>
				</div>
			</div>
		);
	};

	const cameraStarted = (stream) => {
		setStream(stream);
		handleFrontCamera();
	};

	const checkError = () => {

		navigator.permissions.query({ name: 'camera' }).then((res) => {
			if (res.state === "denied") {
				logData({ uid, company, message: "camera permission denied" })
				if (OS === 'iOS') {
					setErrorType({ isError: true, type: "ios" });
				} else {
					setErrorType({ isError: true, type: "android" });
				}
			}
		}).catch((error) => {
			logData({ uid, company, message: "camera permission denied" })
			setErrorType({ isError: true, type: "ios" });
		});

	};

	const handleFrontCamera = () => {
		OS === "iOS" ? handleWithFacialMode() : handleWithLabel();
	};

	const handleWithFacialMode = () => {
		navigator.mediaDevices.getUserMedia({ video: { facingMode: FACING_MODES.USER } })
			.then((stream) => {
				setFacialMode(FACING_MODES.USER);
				logData({ uid, company, message: "ios front camera found" })
			})
			.catch((error) => {
				sendToDevelopers({ info: { ...getCompleteDeviceDetails(), uid, company, error: JSON.stringify(error) } })

				setFrontCameraError(true);
				setFacialMode(FACING_MODES.ENVIRONMENT);
				logData({ uid, company, message: "ios front camera not found. Fallback to rear camera" })
			});
	};

	const handleWithLabel = () => {
		navigator.mediaDevices.enumerateDevices().then(devices => {
			const videoDevices = devices.filter(device => device.kind === 'videoinput');
			const frontCamera = videoDevices.find(device => device.getCapabilities().facingMode?.includes("user"));
			if (frontCamera) {
				setFacialMode(frontCamera.deviceId); // Set the deviceId directly
				logData({ uid, company, message: "android front camera found" })
			} else {
				sendToDevelopers({ info: { ...getCompleteDeviceDetails(), uid, company, error: JSON.stringify(videoDevices) } })
				setFacialMode(FACING_MODES.ENVIRONMENT); // Fallback to rear camera
				setFrontCameraError(true)
				logData({ uid, company, message: "android front camera not found. Fallback to rear camera" })
				// mailDevelopers()
			}
		}).catch(err => {
			logData({ uid, company, message: `error accessing android camera. ${err.message}` })
			console.log(err)
		});
	};

	const DemoScreen = () => {
		logData({ uid, company, message: "viewing demo screen" })
		return (
			<div className="relative w-full h-full bg-black flex flex-col items-center justify-center">
				<div className="absolute top-4 left-4 z-10">
					<button onClick={handleBack} className="text-white">
						<ArrowLeft size={24} />
					</button>
				</div>
				<img src={demoCapture} alt="demo" className="w-full h-full object-cover" />

				<img src={shadow} alt="shadow" className="absolute top-0 left-0 w-full h-full object-cover" />
				<div className="absolute bottom-10 w-full flex flex-col items-center">
					<p className="text-white text-center text-lg mb-4 px-5 font-CirceRounded-Light md:text-xl">Position your face on the phone screen as shown in the guide above</p>
					<button onClick={() => {
						setShowDemoScreen(false)
						logData({ uid, company, message: "moving to camera capture" })
					}}
						className="bg-white text-black font-bold py-2 px-8 rounded-full md:py-3 md:px-10">
						Got it
					</button>
				</div>
			</div>
		)
	}

	const reTake = () => {
		logData({ uid, company, message: "re-taking photo" })
		setCapturedImage(null);
	}

	return (
		<>
			{
				showDemoScreen ? <DemoScreen /> :
					capturedImage ? <Preview imageDataUri={capturedImage} onRetake={reTake} goBack={handleBack} /> :
						<div className="relative w-full h-screen bg-black flex flex-col items-center justify-center">
							{errorType.isError && <PermissionPopup info={errorType} setErrorType={setErrorType} />}
							<div className="absolute top-4 left-4 z-10">
								<button onClick={handleBack} className="text-white">
									<ArrowLeft size={24} />
								</button>
							</div>
							<Camera
								onTakePhotoAnimationDone={handleTakePhoto}
								onCameraStart={cameraStarted}
								onCameraError={checkError}
								idealFacingMode={facialMode}
								isFullscreen={false}
								isMaxResolution={true}
								imageType={IMAGE_TYPES.JPG}
								isImageMirror={facialMode !== FACING_MODES.ENVIRONMENT}
								sizeFactor={1}
								videoConstraints={{
									facingMode: { exact: "user" },
									width: { ideal: window.innerWidth },
									height: { ideal: window.innerHeight },
									aspectRatio: { ideal: window.innerWidth / window.innerHeight }
								}}
								imageCompression={0.6}
								className="WebCam"
							/>
							{!errorType.isError && frontCameraError && <FrontCameraError back={setFrontCameraError} />}
							<OvuleShape />
							<TopLayer />
						</div>
			}
		</>
	);
};

export default CameraCapture;
